/**
 * WindowSpec — renderer for a SELECT-list item that contains a window
 * function (`func(...) OVER (…)` or `func(...) OVER w_name`). Renders the
 * function-call text plus an expandable panel showing the OVER body. The
 * chevron toggle (UI state) is always enabled so users can show / hide the
 * body; default-collapsed.
 *
 * The expanded panel's body/edit affordance is delegated to
 * `WindowOverEditor`: opaque `<pre>` text with a disabled `[edit fields]`
 * button per § 1.4 when no `editContext` is supplied, or a raw-text
 * `<textarea>` editor (the `modify-select-window` op) when it is. v1 does not
 * parse PARTITION BY / ORDER BY / frame sub-clauses either way — the raw-text
 * editor replaces the WHOLE `OVER …` clause verbatim.
 *
 * Collapsed-header truncation kit (VQE Polish primitive #9 — ANA-05/09/14/18):
 * the summary used to hard-cap at 40 chars regardless of available row width,
 * which hid the frame clause (and sometimes the ORDER BY target) behind an
 * ellipsis with a screenful of space left. It now renders as
 * `min-w-0 flex-1 truncate` (CSS ellipsis sized to the real row, not a fixed
 * character count) plus structural mini-badges (`PARTITION` / `ORDER` /
 * `ROWS`|`RANGE`) from a light keyword scan of the OVER body, so a collapsed
 * chip always reveals whether a frame clause exists even when the text itself
 * ellipsizes. The full-body tooltip is unchanged. This only touches the
 * COLLAPSED header — the expanded body still delegates to `WindowOverEditor`
 * (Wave-1 rewrites that editor; not touched here).
 */
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { useVqeStore } from '../../store/vqeStore';
import type { VqeEditContext } from '../../edit/editContext';
import { VqeTooltip } from '../common/VqeTooltip';
import type { WindowItem } from './extractWindowFunction';
import { WindowOverEditor } from './WindowOverEditor';

export interface WindowSpecProps {
  readonly item: WindowItem;
  readonly statementId: string;
  readonly itemIndex: number;
  readonly testIdPrefix: string;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
}

export function WindowSpec({ item, statementId, itemIndex, testIdPrefix, options, editContext }: WindowSpecProps) {
  const expansionKey = `${statementId}:window:${itemIndex}`;
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);

  const hasOverBody = item.overBody !== null;
  const hasNamedWindow = item.namedWindow !== null;

  return (
    <span
      className="flex min-w-0 flex-1 flex-col gap-1.5"
      data-testid={testIdPrefix}
      data-has-over-body={hasOverBody ? 'true' : 'false'}
      data-has-named-window={hasNamedWindow ? 'true' : 'false'}
    >
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpansion(expansionKey, !expanded)}
          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          data-testid={`${testIdPrefix}-toggle`}
          aria-label={expanded ? 'Collapse OVER spec' : 'Expand OVER spec'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <span
          className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
          data-testid={`${testIdPrefix}-badge`}
          title="Window function"
        >
          window
        </span>
        {/* A long call (`PERCENTILE_CONT(0.5) WITHIN GROUP (…)`) must ellipsize
            inside the chip too — otherwise it becomes the row's min-content and
            re-creates the very overflow the summary's truncation just fixed. */}
        <VqeTooltip content={item.funcText} shrink>
          <span
            className="min-w-0 truncate font-mono text-xs"
            data-testid={`${testIdPrefix}-func`}
          >
            {item.funcText}
          </span>
        </VqeTooltip>
        <span className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-over-keyword`}>
          OVER
        </span>
        {hasNamedWindow ? (
          <span
            className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px]"
            data-testid={`${testIdPrefix}-named-window`}
          >
            {item.namedWindow}
          </span>
        ) : (
          <>
            <OverFrameBadges body={item.overBody} testIdPrefix={testIdPrefix} />
            {/* `shrink` is LOAD-BEARING, not cosmetic: without it `VqeTooltip`
                wraps the summary in a bare `inline-flex` (min-width:auto), so
                the wrapper — not the inner span — is the flex item, its
                min-content is the WHOLE nowrap predicate, the inner `truncate`
                never engages, and the chip's alias pill + remove × get pushed
                outside the chip border (VQE-UI bug 1). */}
            <VqeTooltip content={item.overBody ?? ''} shrink>
              <span
                className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground"
                data-testid={`${testIdPrefix}-over-summary`}
              >
                ({collapseWhitespace(item.overBody)})
              </span>
            </VqeTooltip>
          </>
        )}
      </span>

      {expanded ? (
        <span className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/30 p-2" data-testid={`${testIdPrefix}-body`}>
          <WindowOverEditor item={item} itemIndex={itemIndex} testIdPrefix={testIdPrefix} options={options} editContext={editContext} />
        </span>
      ) : null}
    </span>
  );
}

function collapseWhitespace(body: string | null): string {
  if (body === null) return '';
  return body.replace(/\s+/g, ' ').trim();
}

const PARTITION_BY_RE = /\bpartition\s+by\b/i;
const ORDER_BY_RE = /\border\s+by\b/i;
const FRAME_RE = /\b(rows|range)\b/i;

interface OverFrameBadgesProps {
  readonly body: string | null;
  readonly testIdPrefix: string;
}

/**
 * Structural mini-badges for the collapsed OVER header — a light keyword
 * scan (not a full parse) so a `PARTITION BY` / `ORDER BY` / frame clause
 * stays visible even once the truncating summary text ellipsizes it away.
 * Rendered BEFORE the summary text (not after) so they never compete with it
 * for the flex row's remaining width and always stay on-screen.
 */
function OverFrameBadges({ body, testIdPrefix }: OverFrameBadgesProps) {
  if (!body) return null;
  const hasPartition = PARTITION_BY_RE.test(body);
  const hasOrder = ORDER_BY_RE.test(body);
  const frameMatch = FRAME_RE.exec(body);
  const frame = frameMatch ? (frameMatch[1].toUpperCase() as 'ROWS' | 'RANGE') : null;
  if (!hasPartition && !hasOrder && !frame) return null;

  return (
    <span className="flex shrink-0 items-center gap-1" data-testid={`${testIdPrefix}-frame-badges`}>
      {hasPartition ? (
        <span
          className="rounded bg-muted px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
          data-testid={`${testIdPrefix}-partition-badge`}
        >
          PARTITION
        </span>
      ) : null}
      {hasOrder ? (
        <span
          className="rounded bg-muted px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
          data-testid={`${testIdPrefix}-order-badge`}
        >
          ORDER
        </span>
      ) : null}
      {frame ? (
        <span
          className="rounded bg-amber-100 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
          data-testid={`${testIdPrefix}-frame-badge`}
        >
          {frame}
        </span>
      ) : null}
    </span>
  );
}
